import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  Paper,
  TextField,
  Typography,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Stack,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import API from "../../api/axiosConfig";
import { useToast } from "../../context/ToastContext";
import AutocompleteUsers from "../../components/AutocompleteUsers"

export default function CandidateCreationPage() {
  const showToast = useToast();

  // Form State
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [currentEmail, setCurrentEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [referral, setReferral] = useState("");
  const [desiredPosition, setDesiredPosition] = useState("");
  const [yearsExperience, setYearsExperience] = useState("");
  const [cleared, setCleared] = useState("NONE");
  const [poly, setPoly] = useState("NONE");
  const [polyDate, setPolyDate] = useState("");

  // Validation States
  const [validated, setValidated] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Auto-format phone input to (XXX) XXX-XXXX
  const handlePhoneChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "");
    let formatted = raw;
    if (raw.length >= 4 && raw.length < 7) {
      formatted = `(${raw.slice(0, 3)}) ${raw.slice(3)}`;
    } else if (raw.length >= 7) {
      formatted = `(${raw.slice(0, 3)}) ${raw.slice(3, 6)}-${raw.slice(6, 10)}`;
    }
    setPhoneNumber(formatted);

    // Clear phone error on edit
    if (fieldErrors.phoneNumber) {
      setFieldErrors((prev) => ({ ...prev, phoneNumber: "" }));
    }
  };

  const clearForm = () => {
    setFirstName("");
    setLastName("");
    setCurrentEmail("");
    setPhoneNumber("");
    setReferral("");
    setDesiredPosition("");
    setYearsExperience("");
    setPolyDate("");
    setCleared("NONE");
    setPoly("NONE");
    setValidated(false);
    setFieldErrors({});
  };

  // Enable/disable submit button based on required fields
  useEffect(() => {
    const isBasicValid =
      firstName.trim() !== "" &&
      lastName.trim() !== "" &&
      currentEmail.trim() !== "" &&
      phoneNumber.trim() !== "";
    setValidated(isBasicValid);
  }, [firstName, lastName, currentEmail, phoneNumber]);



  // Frontend Pre-validation Checks
  const validateFrontend = () => {
    const errors = {};

    if (referral && !referral.toLowerCase().trim().endsWith("@s3k.io")) {
      errors.referral = "Referral email must end with @s3k.io";
    }

    if (!phoneNumber) {
      errors.phoneNumber = "Must enter valid phone number";
    } else {
      const digits = phoneNumber.replace(/\D/g, "");
      if (digits.length !== 10) {
        errors.phoneNumber = "Phone number must be a valid 10-digit number";
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // SUBMIT HANDLER
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateFrontend()) {
      showToast("Please fix the errors before submitting", "error");
      return;
    }

    const fullName = `${firstName.trim()} ${lastName.trim()}`;

    try {
      await API.post("/api/candidates/add", {
        name: fullName,
        email: currentEmail.trim(),
        phoneNumber: phoneNumber.trim(),
        desiredPosition: desiredPosition.trim(),
        referral: referral.trim(),
        yearsExperience: yearsExperience ? Number(yearsExperience) : null,
        clearanceType: cleared,
        polyType: poly,
        polyDate: polyDate || null,
      });

      clearForm();
      showToast(`${fullName} successfully added as candidate!`, "success");
    } catch (err) {
      const resData = err.response?.data;

      // Handles ErrorMessage DTO ({ error: "fieldName", message: "Error msg" })
      if (resData?.error && resData?.message) {
        setFieldErrors({ [resData.error]: resData.message });
        showToast(resData.message, "error");
      } else {
        showToast("Failed to create candidate. Please check inputs.", "error");
      }
    }
  };

  return (
    <Box sx={{ maxWidth: 850, mx: "auto", my: 4, px: 2 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
          <PersonAddIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5" fontWeight="bold">
            Add New Hiring Candidate
          </Typography>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2.5}>
            {/* --- SECTION 1: PERSONAL DETAILS --- */}
            <Grid item xs={12}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight="bold"
                sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}
              >
                Personal Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                variant="outlined"
                value={firstName}
                fullWidth
                onChange={(e) => setFirstName(e.target.value)}
                required
                error={Boolean(fieldErrors.name)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                variant="outlined"
                value={lastName}
                fullWidth
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Candidate Email"
                variant="outlined"
                type="email"
                value={currentEmail}
                fullWidth
                onChange={(e) => setCurrentEmail(e.target.value)}
                required
                error={Boolean(fieldErrors.email)}
                helperText={fieldErrors.email}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone Number"
                type="tel"
                variant="outlined"
                value={phoneNumber}
                fullWidth
                onChange={handlePhoneChange}
                placeholder="(555) 000-0000"
                error={Boolean(fieldErrors.phoneNumber)}
                helperText={fieldErrors.phoneNumber}
                inputProps={{ maxLength: 14 }}
              />
            </Grid>

            <Grid item xs={12}>
              <AutocompleteUsers
                label="Referral Employee Email"
                value={referral}
                valueKey="email"
                freeSolo // Allows typing an email directly or selecting from dropdown
                error={fieldErrors.referral}
                helperText={
                  fieldErrors.referral || "Must be an @s3k.io email address"
                }
                // Custom filter: Matches input against name OR email
                filterOptions={(options, { inputValue }) => {
                  const query = inputValue.toLowerCase().trim();
                  if (!query) return options;

                  return options.filter((user) => {
                    // Handles string options if freeSolo is active
                    if (typeof user === "string") {
                      return user.toLowerCase().includes(query);
                    }

                    const nameMatch = user.name?.toLowerCase().includes(query);
                    const emailMatch = user.email
                      ?.toLowerCase()
                      .includes(query);

                    return nameMatch || emailMatch;
                  });
                }}
                onChange={(selectedEmail) => {
                  setReferral(selectedEmail || "");
                  if (fieldErrors.referral) {
                    setFieldErrors((prev) => ({ ...prev, referral: "" }));
                  }
                }}
              />
            </Grid>

            {/* --- SECTION 2: PROFESSIONAL BACKGROUND --- */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight="bold"
                sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}
              >
                Professional Experience
              </Typography>
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                label="Desired Position"
                variant="outlined"
                value={desiredPosition}
                fullWidth
                onChange={(e) => setDesiredPosition(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Years of Experience"
                variant="outlined"
                type="number"
                value={yearsExperience}
                fullWidth
                onChange={(e) => setYearsExperience(e.target.value)}
                inputProps={{ min: 0 }}
              />
            </Grid>

            {/* --- SECTION 3: SECURITY & CLEARANCE --- */}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight="bold"
                sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}
              >
                Security Credentials
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="clearance-select-label">
                  Security Clearance
                </InputLabel>
                <Select
                  labelId="clearance-select-label"
                  value={cleared}
                  label="Security Clearance"
                  onChange={(e) => setCleared(e.target.value)}
                >
                  <MenuItem value="NONE">No Clearance</MenuItem>
                  <MenuItem value="CONFIDENTIAL">Confidential</MenuItem>
                  <MenuItem value="SECRET">Secret</MenuItem>
                  <MenuItem value="TS_SCI">Top Secret / SCI</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="polygraph-select-label">Polygraph</InputLabel>
                <Select
                  labelId="polygraph-select-label"
                  value={poly}
                  label="Polygraph"
                  onChange={(e) => setPoly(e.target.value)}
                >
                  <MenuItem value="NONE">None</MenuItem>
                  <MenuItem value="COUNTER_INTELLIGENCE">
                    Counter Intelligence (CI)
                  </MenuItem>
                  <MenuItem value="FULL_SCOPE">Full Scope (FS)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Date of Polygraph"
                type="date"
                variant="outlined"
                fullWidth
                value={polyDate}
                onChange={(e) => setPolyDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* --- SUBMIT BUTTON --- */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                type="submit"
                disabled={!validated}
                fullWidth
                sx={{ py: 1.5, fontWeight: "bold" }}
              >
                Submit Candidate
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
}
